/**
 * ConfirmDialog
 * ─────────────
 * Custom modal replacement for native window.confirm().
 * Kullanıcıya net bir onay adımı sunar; Evet/OK basılmadan aksiyon yapılmaz.
 *
 * Props:
 *   open: boolean
 *   title, message
 *   confirmLabel, cancelLabel
 *   onConfirm: async () => void  (Promise destekler; loading state gösterir)
 *   onCancel
 *   variant: 'danger' | 'default'
 *   testId: optional data-testid base
 */
import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

export default function ConfirmDialog({
  open,
  title = 'Emin misiniz?',
  message = '',
  confirmLabel = 'Evet',
  cancelLabel = 'Vazgeç',
  onConfirm,
  onCancel,
  variant = 'danger',
  testId = 'confirm-dialog',
}) {
  const [loading, setLoading] = useState(false);
  if (!open) return null;

  const colors = variant === 'danger'
    ? { btn: 'bg-red-500 hover:bg-red-600', iconBg: 'bg-red-100', icon: 'text-red-600' }
    : { btn: 'bg-indigo-500 hover:bg-indigo-600', iconBg: 'bg-indigo-100', icon: 'text-indigo-600' };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid={testId}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-start gap-3 p-6">
          <div className={`h-10 w-10 rounded-full ${colors.iconBg} flex items-center justify-center flex-shrink-0`}>
            <AlertTriangle className={`h-5 w-5 ${colors.icon}`} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            {message && <p className="text-sm text-gray-500 mt-1.5 leading-relaxed whitespace-pre-wrap">{message}</p>}
          </div>
          <button
            type="button"
            onClick={() => !loading && onCancel?.()}
            className="p-1 rounded-lg hover:bg-gray-100 text-gray-400 flex-shrink-0"
            aria-label="close"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => !loading && onCancel?.()}
            disabled={loading}
            data-testid={`${testId}-cancel`}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800 rounded-xl transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            data-testid={`${testId}-confirm`}
            className={`${colors.btn} text-white font-semibold px-5 py-2 rounded-xl text-sm disabled:opacity-60 transition-colors`}
          >
            {loading ? '...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
